import { useCallback } from "react"
import { parseAbi, encodeFunctionData, type Address } from "viem"
import { useWallet, useChainSpecificWallet } from "wallet"
import { isFeatureSupported } from "wallet/config/gasSponsorship"
import { SX_EXTERNAL_PARTNER_ID } from "@/config/env"
import { fetchOrders, prefillOrder } from "@/SX/api"
import useChainProviderHook from "@/hooks/useChainProvider"
import { SX_CONFIG } from "../constants"
import { arbitrum, berachain } from "viem/chains"

interface SXBetPlacementProps {
  amount: string
  bettingOnOutcomeOne: boolean
  writeContract: any
  allOdds: any
  marketHash: string
}

interface PrefillOrderPayload {
  chainId: number
  taker: string
  orderHashes: string[]
  takerAmounts: string[]
  externalPartnerId?: string
}

interface OrderAllocation {
  order: any
  // allocatedAmount: string
  takerAmount: number
  // returnAmountWei: string
}

/**
 * Allocates bet amount across available orders based on their capacity
 */
const allocateOrders = (
  amount: string,
  filteredOrders: any[]
): OrderAllocation[] => {
  const userAmount = parseFloat(amount)
  const sortedOrders = filteredOrders.sort((a: any, b: any) => {
    return parseFloat(b.percentageOdds) - parseFloat(a.percentageOdds)
  })

  const allocations: OrderAllocation[] = []
  let remainingAmount = userAmount * 10 ** 6

  for (const order of sortedOrders) {
    if (remainingAmount <= 0) {
      break
    }

    const { totalBetSize, fillAmount, percentageOdds } = order
    const remainingTakersSpace = Math.round(
      ((totalBetSize - fillAmount) * 10 ** 20) / percentageOdds -
        (totalBetSize - fillAmount)
    )

    const availableAmount = Math.min(remainingAmount, remainingTakersSpace)

    let takerAmount = 0
    if (remainingAmount >= remainingTakersSpace) {
      takerAmount = order.totalBetSize - order.fillAmount
    } else {
      const value =
        (Number(remainingAmount) * parseFloat(order.percentageOdds)) /
        (10 ** 20 - parseFloat(order.percentageOdds))
      takerAmount = Math.floor(value)
    }
    console.log({
      remainingAmount,
      remainingTakersSpace,
      percentageOdds: order.percentageOdds,
      takerAmount,
    })
    // if (availableAmount > 0) {
    //   const { takerAmount, returnAmountWei } = calculateAmounts({
    //     amount: availableAmount.toString(),
    //     percentageOdds: order.percentageOdds,
    //   })

    console.log({ takerAmount })
    allocations.push({
      order,
      takerAmount,
    })

    remainingAmount -= availableAmount
    // }
  }

  return allocations
}

export const useSXBetPlacement = () => {
  const { account, isAAWallet } = useWallet()
  const { chainId } = useChainProviderHook()

  // Get gas sponsorship functionality
  const { executeGaslessTransaction, hasGasSponsorship } =
    useChainSpecificWallet()

  // Check if we can use gasless transactions for bets on this chain
  // Only use gas sponsorship for AA wallets, not regular wallets
  const canUseBetSponsorship =
    chainId &&
    isFeatureSupported(chainId, "sponsorBets") &&
    hasGasSponsorship &&
    !!executeGaslessTransaction &&
    isAAWallet // Only use gas sponsorship for AA wallets

  const {
    CONTRACTS: { BET_TOKEN, PLACEBET },
    TOKEN_DECIMALS,
    NETWORK_FEES,
  } = SX_CONFIG[chainId]

  const placeSXBet = useCallback(
    async ({
      amount,
      bettingOnOutcomeOne,
      writeContract,
      allOdds, // TODO
      marketHash,
    }: SXBetPlacementProps) => {
      const orders = await fetchOrders([marketHash], chainId)

      const filteredOrders =
        orders?.data?.filter((order: any) => {
          return order.isMakerBettingOutcomeOne === !bettingOnOutcomeOne
        }) || []

      if (!filteredOrders.length) {
        throw new Error("No suitable orders available")
      }

      // Allocate orders based on user amount
      const orderAllocations = allocateOrders(amount, filteredOrders)

      if (orderAllocations.length === 0) {
        throw new Error("No orders can accommodate the requested amount")
      }

      // Prepare prefill payload with multiple orders
      const preFillPayload: PrefillOrderPayload = {
        chainId: Number(chainId),
        taker: account,
        orderHashes: orderAllocations.map(
          allocation => allocation.order.orderHash
        ),
        takerAmounts: orderAllocations.map(allocation =>
          allocation.takerAmount.toString()
        ),
        ...((chainId === berachain.id || chainId === arbitrum.id) && {
          externalPartnerId: SX_EXTERNAL_PARTNER_ID,
        }),
      }

      const orderPreFill = await prefillOrder(preFillPayload, chainId)

      // Calculate total amounts across all allocations

      const totalNetReturnWei =
        (orderAllocations.reduce(
          (total, allocation) => total + allocation.takerAmount,
          0
        ) *
          10 ** TOKEN_DECIMALS) /
        10 ** 6

      const stakeAmountWei = Number(amount) * 10 ** TOKEN_DECIMALS

      const feesWei = Math.floor((totalNetReturnWei * NETWORK_FEES) / 100)
      const totalReturnAmountWei = stakeAmountWei + totalNetReturnWei - feesWei

      const blockchainPayload = {
        id: orderPreFill.data.uuid,
        tokenAddress: BET_TOKEN,
        predictedOutcome: bettingOnOutcomeOne ? 1 : 2,
        stakeAmount: BigInt(stakeAmountWei),
        returnAmount: BigInt(totalReturnAmountWei),
      }

      console.group("-------- crosschainOrderRequest placeBet params")
      console.log("UUID: ", orderPreFill.data.uuid)
      console.log("Token Address: ", BET_TOKEN)
      console.log("Betting Outcome: ", bettingOnOutcomeOne ? 1 : 2)
      console.log("preFillPayload", preFillPayload)
      console.log("blockchainPayload", blockchainPayload)
      console.log("Return Amount (Wei): ", totalReturnAmountWei)
      console.log("Order Allocations: ", orderAllocations)
      console.groupEnd()

      const { id, tokenAddress, predictedOutcome, stakeAmount, returnAmount } =
        blockchainPayload

      const abi = parseAbi([
        `function placeBet(bytes32,address,uint8,uint256,uint256)`,
      ])

      // Try gas sponsorship first if available
      if (canUseBetSponsorship && executeGaslessTransaction) {
        console.log(
          "🎯 [useSXBetPlacement] Using gas sponsorship for SX bet:",
          {
            chainId,
            contractAddress: PLACEBET,
            hasGasSponsorship,
            canUseBetSponsorship,
          }
        )

        try {
          // Encode the function call
          const encodedData = encodeFunctionData({
            abi,
            functionName: "placeBet",
            args: [
              id,
              tokenAddress,
              predictedOutcome,
              stakeAmount,
              returnAmount,
            ],
          })

          const txHash = await executeGaslessTransaction(
            PLACEBET as Address,
            encodedData,
            BigInt(0) // SX bets don't send ETH value
          )

          console.log(
            "✅ [useSXBetPlacement] Gas sponsored SX bet successful:",
            {
              txHash,
              explorerUrl:
                chainId === 421614
                  ? `https://sepolia.arbiscan.io/tx/${txHash}`
                  : `https://bartio.beratrail.io/tx/${txHash}`,
            }
          )

          return txHash
        } catch (error) {
          console.error(
            "💥 [useSXBetPlacement] Gas sponsored transaction failed, falling back to regular transaction:",
            error
          )
          // Fall through to regular transaction
        }
      }

      // Regular transaction (fallback or when gas sponsorship is not available)
      console.log("📝 [useSXBetPlacement] Using regular transaction for SX bet")
      const txHash = await writeContract({
        address: PLACEBET,
        abi,
        functionName: "placeBet",
        args: [id, tokenAddress, predictedOutcome, stakeAmount, returnAmount],
      })

      return txHash
    },
    [
      account,
      canUseBetSponsorship,
      executeGaslessTransaction,
      hasGasSponsorship,
      chainId,
      PLACEBET,
    ]
  )

  return {
    placeSXBet,
    // Expose gas sponsorship info for debugging
    hasGasSponsorship,
    canUseBetSponsorship,
    chainSupportsBetSponsorship: chainId
      ? isFeatureSupported(chainId, "sponsorBets")
      : false,
  }
}
